// AppManager.tsx - With individual JOTLU Track sections in navigation

import React, { useState, useEffect } from 'react';
import { 
  doc, 
  getDoc,
  Firestore 
} from 'firebase/firestore';
import { 
  ref,
  uploadBytes,
  getDownloadURL,
  getStorage
} from 'firebase/storage';
import { Functions } from 'firebase/functions';
import { useAuth } from '../../../hooks/AuthProvider';
import OrganizationSelector from './OrganizationSelector';
import AppList from './AppList';
import PermissionsManagement from './PermissionsManagement';
import NavigationSidebar from './NavigationSidebar';
import JOTLUTrack from './track/index';
import '../../../styles/AppManager.css';

// Permission constants
const DEPLOYMENT_ADMIN_PERMISSION = 'DEPLOYMENT_ADMIN';

// Define interfaces for App data
interface Theme {
  backgroundColor: string;
  textColor: string;
  cardColor: string;
  topbarBackgroundColor: string;
  topbarIconColor: string;
  topbarBorderBottomColor: string;
  sidebarBackgroundColor: string;
  sidebarIconColor: string;
  sidebarIconColorSelected: string;
  sidebarTextColor: string;
  sidebarBorderColor: string;
  borderColor: string;
  categoryIndicatorColor: string;
  cartBackgroundColor: string;
  cartCardColor: string;
  cartTextColor: string;
  cartButtonColor: string;
  cartButtonTextColor: string;
  cartSummaryBorder: string;
  payButtonColor: string;
  payButtonTextColor: string;
  loaderColor: string;
  searchBarColor?: string;
  menuButtons?: boolean;
}

interface ThemeConfig {
  variant: 'modern-light' | 'modern-dark';
  accentColor: string;
  custom?: Theme;
}

interface DomainConfig {
  connected: boolean;
}

interface App {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  icon: string;
  url: string;
  requiredPermission: string;
  region: string;
  type: string;
  status?: string;
  deploymentApproved?: boolean;
  initializationComplete?: boolean;
  adminSetupComplete?: boolean;
  firebaseProjectId?: string;
  firebaseConfig?: FirebaseConfig;
  locationId?: string;
  organizationId: string;
  theme?: ThemeConfig;
  themeConfigured?: boolean;
  domainConfigured?: boolean;
  iconConfigured?: boolean;
  appIcon?: string;
  domains?: Record<string, DomainConfig>;
}

interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

interface MetadataField {
  id: string;
  name: string;
  enabled?: boolean;
  organizationId?: string;
}

interface ThemeUpdateRequest {
  id: string;
  appId: string;
  appName: string;
  requestedBy: string;
  requestedAt: Date | any;
  themeData: Theme;
  status: 'pending' | 'approved' | 'rejected';
}

interface AppManagerProps {
  db: Firestore;
  storage: ReturnType<typeof getStorage>;
  functions: Functions;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  setLoading: (loading: boolean) => void;
}

export type { Theme, ThemeConfig, App, FirebaseConfig, MetadataField, ThemeUpdateRequest, DomainConfig };

const AppManager: React.FC<AppManagerProps> = ({ 
  db, 
  storage,
  functions, 
  onError, 
  onSuccess, 
  setLoading 
}) => {
  const { currentUser, hasPermission, userPermissions } = useAuth();
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('');
  const [viewMode, setViewMode] = useState<'organizations' | 'list' | 'permissions' | 'transactions' | 'clients' | 'vendors' | 'invoices' | 'expenses'>('organizations');
  
  const canApproveDeployment = hasPermission(DEPLOYMENT_ADMIN_PERMISSION);

  // Get organizations the user has access to
  const getUserOrganizations = (): string[] => {
    const organizations = new Set<string>();
    
    userPermissions.forEach(permission => {
      if (permission.startsWith('organizations_')) {
        const parts = permission.split('_');
        if (parts.length >= 3) {
          const orgId = parts[1];
          organizations.add(orgId);
        }
      }
    });

    return Array.from(organizations);
  };

  // Check if user has admin access to the current organization
  const hasAdminAccessToCurrentOrg = (): boolean => {
    if (!selectedOrganizationId) return false;
    return userPermissions.some(permission => 
      permission === `organizations_${selectedOrganizationId}_admin`
    );
  };

  // Handle organization selection
  const handleSelectOrganization = (organizationId: string) => {
    setSelectedOrganizationId(organizationId);
    setViewMode('list');
  };

  // Handle going back to organization selector
  const handleBackToOrganizations = () => {
    setSelectedOrganizationId('');
    setViewMode('organizations');
  };

  // Check if user has access to any organizations on mount
  useEffect(() => {
    const organizations = getUserOrganizations();
    
    if (organizations.length === 0) {
      onError('You do not have access to any organizations. Please contact an administrator.');
      return;
    }

    // If user has access to only one organization, auto-select it
    if (organizations.length === 1) {
      handleSelectOrganization(organizations[0]);
    }
  }, [userPermissions, onError]);

  // Navigation handlers
  const handleBackToList = () => {
    setViewMode('list');
  };

  const handleViewPermissions = () => {
    setViewMode('permissions');
  };

  const handleViewTransactions = () => {
    setViewMode('transactions');
  };

  const handleViewClients = () => {
    setViewMode('clients');
  };

  const handleViewVendors = () => {
    setViewMode('vendors');
  };

  const handleViewInvoices = () => {
    setViewMode('invoices');
  };

  const handleViewExpenses = () => {
    setViewMode('expenses');
  };

  // Get the active navigation item based on current view
  const getActiveNavigationItem = (): 'app-management' | 'permissions' | 'transactions' | 'clients' | 'vendors' | 'invoices' | 'expenses' => {
    switch (viewMode) {
      case 'permissions':
        return 'permissions';
      case 'transactions':
        return 'transactions';
      case 'clients':
        return 'clients';
      case 'vendors':
        return 'vendors';
      case 'invoices':
        return 'invoices';
      case 'expenses':
        return 'expenses';
      default:
        return 'app-management';
    }
  };

  // Show sidebar for all views except organizations selector
  const showSidebar = viewMode !== 'organizations';

  return (
    <div className="app-manager">
      {viewMode === 'organizations' ? (
        <OrganizationSelector 
          onSelectOrganization={handleSelectOrganization}
        />
      ) : (
        <div className="applist-container">
          {/* Left Sidebar Navigation */}
          {showSidebar && (
            <NavigationSidebar
              selectedOrganizationId={selectedOrganizationId}
              activeItem={getActiveNavigationItem()}
              onBackToOrganizations={handleBackToOrganizations}
              onNavigateToApps={handleBackToList}
              onNavigateToPermissions={hasAdminAccessToCurrentOrg() ? handleViewPermissions : undefined}
              onNavigateToTransactions={handleViewTransactions}
              onNavigateToClients={handleViewClients}
              onNavigateToVendors={handleViewVendors}
              onNavigateToInvoices={handleViewInvoices}
              onNavigateToExpenses={handleViewExpenses}
            />
          )}

          {/* Main Content Area */}
          <div className="applist-main-content" style={{overflowY: "scroll"}}>
            {viewMode === 'list' && (
              <AppList
                db={db}
                functions={functions}
                onError={onError}
                onSuccess={onSuccess}
                setLoading={setLoading}
                onSelectApp={() => {}} // Simplified - no app selection needed
                canApproveDeployment={canApproveDeployment}
                onViewAdminPanel={undefined} // Removed admin panel
                onViewPermissions={hasAdminAccessToCurrentOrg() ? handleViewPermissions : undefined}
                selectedOrganizationId={selectedOrganizationId}
                onBackToOrganizations={handleBackToOrganizations}
              />
            )}

            {viewMode === 'permissions' && (
              <PermissionsManagement
                selectedOrganizationId={selectedOrganizationId}
                onBackToOrganizations={handleBackToOrganizations}
                onNavigateToApps={handleBackToList}
                onNavigateToAdmin={() => {}} // Removed admin panel
                onNavigateToPermissions={handleViewPermissions}
                db={db}
                functions={functions}
                onError={onError}
                onSuccess={onSuccess}
                setLoading={setLoading}
              />
            )}

            {/* JOTLU Track Views */}
            {(viewMode === 'transactions' || viewMode === 'clients' || viewMode === 'vendors' || viewMode === 'invoices' || viewMode === 'expenses') && (
              <JOTLUTrack
                db={db}
                functions={functions}
                storage={storage}
                selectedOrganizationId={selectedOrganizationId}
                onError={onError}
                onSuccess={onSuccess}
                setLoading={setLoading}
                onBackToOrganizations={handleBackToOrganizations}
                currentView={viewMode as 'transactions' | 'clients' | 'vendors' | 'invoices' | 'expenses'}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AppManager;